import { Injectable } from '@angular/core';
import { filter, switchMap } from 'rxjs/operators';

import { DevAuthService } from 'src/app/core/auth/dev-auth.service';
import { AccountApiService, AccountSettings } from 'src/app/core/services/account-api.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { ThemeService } from 'src/app/core/services/theme.service';

/** localStorage flag: this browser already handed its own pre-move choices to the account. */
const MIGRATED_KEY = 'developer.settingsMigrated';

/**
 * Language and appearance are chosen once in My-SPA and apply in every app, so the account is the single source
 * of truth and this app only reads it (same rule as Portal-SPA's AccountSettingsService).
 *
 * Before the move each app kept its own choice. The first sync in a browser hands a language that differs from
 * the account, or an appearance the account does not have yet, to the account once, so nobody loses a setting.
 * After that the account value always wins. A failing read keeps what is showing: this app works without it.
 */
@Injectable({ providedIn: 'root' })
export class AccountSettingsService {
  private synced = false;
  private watching = false;

  constructor(
    private readonly auth: DevAuthService,
    private readonly api: AccountApiService,
    private readonly language: LanguageService,
    private readonly theme: ThemeService,
  ) {}

  /** Reads the account once per page load after sign-in, and again whenever the tab becomes visible. */
  start(): void {
    if (this.watching) return;
    this.watching = true;

    this.auth.isAuthenticated$.pipe(
      filter(Boolean),
      switchMap(() => this.api.getMe()),
    ).subscribe({ next: user => this.sync(user), error: () => { /* keep what is showing */ } });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.synced) this.refresh();
    });
  }

  sync(user: AccountSettings): void {
    if (this.synced) return;
    this.synced = true;

    const push: AccountSettings = this.migrated() ? {} : this.localChoices(user);
    this.language.applyServerLanguage(push.language ?? user.language);
    this.theme.applyServerTheme(push.theme ?? user.theme);

    if (Object.keys(push).length === 0) {
      this.markMigrated();
      return;
    }
    this.api.updateProfile(push).subscribe({
      next: () => this.markMigrated(),
      error: () => { /* the next page load tries again; until then this browser keeps its own choice */ },
    });
  }

  private refresh(): void {
    this.api.getMe().subscribe({
      next: user => {
        this.language.applyServerLanguage(user.language);
        this.theme.applyServerTheme(user.theme);
      },
      error: () => { /* keep what is showing */ },
    });
  }

  private localChoices(user: AccountSettings): AccountSettings {
    const choices: AccountSettings = {};
    const language = this.language.savedLanguage;
    if (language && language !== user.language) choices.language = language;
    const theme = this.theme.storedPreference;
    if (theme && !user.theme) choices.theme = theme;
    return choices;
  }

  private migrated(): boolean {
    try { return localStorage.getItem(MIGRATED_KEY) === '1'; } catch { return true; }
  }

  private markMigrated(): void {
    try { localStorage.setItem(MIGRATED_KEY, '1'); } catch { /* storage unavailable */ }
  }
}
